import { Alert, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { useBusiness } from '../contexts/BusinessContext';

type TenantRow = {
  tenant_id: string;
  name: string;
  status?: string;
};

export default function AdminTenantsPage() {
  const { activeTenantId, setActiveTenantId, setActiveEntityId, setActiveConfigId } = useBusiness();
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [error, setError] = useState<string>('');
  const [createError, setCreateError] = useState<string>('');
  const [info, setInfo] = useState<string>('');
  const [tenantId, setTenantId] = useState<string>('t_new');
  const [tenantName, setTenantName] = useState<string>('New Tenant');

  const load = async () => {
    setError('');
    const resp = await apiFetch('/api/business/tenants');
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    const data = (await resp.json()) as TenantRow[];
    setRows(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    void load();
  }, []);

  const create = async () => {
    setCreateError('');
    setInfo('');
    const tid = tenantId.trim();
    const name = tenantName.trim();
    if (!tid || tid.length < 2) {
      setCreateError('Tenant ID is required (min 2 chars).');
      return;
    }
    if (!name) {
      setCreateError('Tenant name is required.');
      return;
    }
    try {
      const resp = await apiFetch('/api/business/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tid, name }),
      });
      if (!resp.ok) {
        setCreateError(await resp.text());
        return;
      }
      setInfo('Tenant created. Click Use to activate it.');
      await load();
    } catch {
      setCreateError('Failed to create tenant.');
    }
  };

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e6ebf1' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: '#012676' }}>Tenants</Typography>
        <Button size="small" variant="outlined" onClick={() => void load()}>
          Refresh
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select the active tenant to scope entities, configs, and data-capture sessions.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 1.5, p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#012676' }}>
          Create Tenant (admin only)
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Tenant ID"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <TextField
            size="small"
            label="Name"
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
            sx={{ minWidth: 220, flex: 1 }}
          />
          <Button variant="contained" onClick={create}>
            Create
          </Button>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Tenant ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.tenant_id}>
                <TableCell>
                  <Button
                    size="small"
                    variant={activeTenantId === r.tenant_id ? 'contained' : 'outlined'}
                    onClick={() => {
                      setActiveTenantId(r.tenant_id);
                      setActiveEntityId(null);
                      setActiveConfigId(null);
                    }}
                  >
                    {activeTenantId === r.tenant_id ? 'Active' : 'Use'}
                  </Button>
                </TableCell>
                <TableCell>{r.tenant_id}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.status || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Paper>
  );
}

